import React, { useCallback, useContext } from 'react'
import ExitIntentModal from './ExitIntentModal'
import useExitIntent from 'hooks/useExitIntent'
import { useDeferredAnalyticsOptimizely } from 'lib/optimizely/useDeferredAnalyticsOptimizely'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import * as Analytics from 'analytics/analytics'
import ModalContext from 'contexts/ModalContext'

interface Props {
  offerId: string
  propertyName?: string
}

function ExitIntentModalContainer(props: Props) {
  const { offerId, propertyName } = props
  const showModal = useContext(ModalContext)

  // Use deferred analytics - experiment is only triggered after inactivity period
  const { variation, triggerAnalytics } = useDeferredAnalyticsOptimizely(
    OptimizelyExperiments.croCaptureBouncingCustomersWithYamlModal,
  )

  // Only enable inactivity detection for users in the treatment variant (not control or off)
  const isExperimentEnabled = getOptimizelyEnabledDecision((variation ?? null) as string | null)

  const handleExitIntent = useCallback(() => {
    // Trigger experiment analytics only after inactivity period
    triggerAnalytics()

    // Track when modal is shown
    Analytics.trackClientEvent({
      subject: 'exit-intent-modal-shown',
      action: 'view',
      category: 'exit-intent-modal',
      type: 'interaction',
      optimizelyEventId: 'exit_intent_modal_impression',
      optimizelyEventKey: 'exit_intent_modal_shown',
    })

    showModal(<ExitIntentModal offerId={offerId} propertyName={propertyName} />).then(() => {
      // Track when modal is closed
      Analytics.trackClientEvent({
        subject: 'exit-intent-modal-closed',
        action: 'close',
        category: 'exit-intent-modal',
        type: 'interaction',
        optimizelyEventId: 'exit_intent_modal_close',
        optimizelyEventKey: 'exit_intent_modal_closed',
      })
    })
  }, [showModal, offerId, propertyName, triggerAnalytics])

  useExitIntent({
    enabled: isExperimentEnabled,
    onExitIntent: handleExitIntent,
  })

  return null
}

export default ExitIntentModalContainer
